import { Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { formatAmount } from '@/utils/format';

import { tableRowStyle } from './tableLayout';

/**
 * Ukupni iznos u traci UKUPNO — često širi od stupca (npr. "7.525.642,57 EUR"), pa ostaje u jednom
 * retku, desno poravnat sa stupcem, a višak se širi ulijevo preko praznih ćelija trake.
 */
export const TotalAmountCell = ({ width, amount }: { width: number; amount: number }) => (
  <div style={{ width, position: 'relative', alignSelf: 'stretch' }}>
    <Text
      fz={13.5}
      fw={700}
      style={{
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        whiteSpace: 'nowrap',
        color: amount < 0 ? 'var(--color-status-danger)' : 'var(--color-text-primary)'
      }}
    >
      {formatAmount(amount)}
    </Text>
  </div>
);

interface ListTotalRowProps {
  /** Širine stupaca liste istim redom kao header (bez stupca akcije). */
  columnWidths: number[];
  /** Iznosi po indeksu stupca (npr. { 3: vrijednost, 4: realizirano }) — svaki ispod svog stupca. */
  amounts: Record<number, number>;
  /** Širina stupca akcije desno (placeholder, da poravnanje ostane isto kao u retcima). */
  actionColumnWidth: number;
}

/**
 * Siva traka "Ukupno" ispod liste (isti stil kao ukupni redak stavki na ugovoru). Iznosi su zbroj
 * TRENUTNE stranice — računa ih stranica iz svojih redaka.
 */
export const ListTotalRow = ({ columnWidths, amounts, actionColumnWidth }: ListTotalRowProps) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        ...tableRowStyle,
        padding: '14px 20px',
        borderTop: '2px solid var(--color-border-default)',
        backgroundColor: 'var(--color-bg-canvas)'
      }}
    >
      {columnWidths.map((width, index) =>
        index === 0 ? (
          <Text
            key={index}
            fz={13}
            fw={700}
            w={width}
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t('common.listTotal')}
          </Text>
        ) : index in amounts ? (
          <TotalAmountCell key={index} width={width} amount={amounts[index]} />
        ) : (
          <div key={index} style={{ width }} />
        )
      )}
      <div style={{ width: actionColumnWidth }} />
    </div>
  );
};

/** Mobilna varijanta — ista siva kartica kao zbroj sati radnika na tjednom izvještaju. */
export const ListTotalCard = ({
  amount,
  details = []
}: {
  amount: number;
  /** Dodatni zbrojevi ispod glavnog (npr. realizirano / preostalo na ugovorima). */
  details?: { label: string; amount: number }[];
}) => {
  const { t } = useTranslation();
  return (
    <Stack gap={6} p={12} style={{ borderRadius: 8, backgroundColor: 'var(--color-bg-surface-2)' }}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('common.listTotal')}
        </Text>
        <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {formatAmount(amount)}
        </Text>
      </Group>
      {details.map((detail) => (
        <Group key={detail.label} justify="space-between" align="center" wrap="nowrap">
          <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
            {detail.label}
          </Text>
          <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {formatAmount(detail.amount)}
          </Text>
        </Group>
      ))}
    </Stack>
  );
};
